import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts } from "../../../Styles";
import { AutoReportAndMoreWidget, AutoReportWidget, GraphWidget, ProgressWidget } from "..";
import { WidgetPlacements } from "../../../Definitions";
import { Graph_QL, System, useAutoReport, Widget } from "../../../Services";
import { Animal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";

interface AllWidgets_Props {
  system?: System
  animals: Animal[];
  widgets: Widget[];
  placement: WidgetPlacements
  overrides?: {
    columnCount?: number
  }
  connect: {
    connection: Graph_QL.Connection
    domain: string
  }
}

export default function AllWidgets({system, placement, widgets, connect, overrides, animals}:AllWidgets_Props) {
  const { t } = useTranslation();

  const { call: getAutoReportInfo, data: autoReportState } = useAutoReport(connect.connection);
  
  const shouldUseAutoReport = useMemo(
    () => widgets.some(w => w.type === 1 || w.type === 2),
    [widgets]
  );
  
  useEffect(() => {
    if (shouldUseAutoReport) getAutoReportInfo({});
  }, [shouldUseAutoReport, getAutoReportInfo]);
  
  const autoReports = useMemo(
    () => (shouldUseAutoReport ? (autoReportState[connect.domain]?.data ?? []) : []),
    [autoReportState, connect.domain, shouldUseAutoReport]
  );

  // const allWidgets = useMemo(() => widgets ?? [], [widgets]);

  const widgetsToShow = useMemo(() => {
    switch (placement) {
      case WidgetPlacements.dashboard:
        return widgets.filter(w => w.placement === 0);
      case WidgetPlacements.kpi:
        return widgets.filter(w => w.placement === 1);
      case WidgetPlacements.report:
        return widgets.filter(w => w.placement === 2);
      case WidgetPlacements.system:
        return widgets.filter(w => w.placement === 3 && w.page === system?.system_id);
      default:
        return [];
    }
  }, [widgets, placement, system]);

  const renderedWidgets = useMemo(() => {
    if (!widgetsToShow.length) return [];
  
    return [...widgetsToShow]
      .sort((a, b) => a.display_order - b.display_order)
      .map(widget => {
        switch (widget.type) {
          case 1: 
            return (
              <AutoReportWidget
                key={widget.widget_id}
                system={system}
                widget={widget}
                animals={animals}
                autoReports={autoReports}
                connect={{
                  connection: connect.connection,
                  domain: connect.domain
                }}
              />
            );
          case 2:
            return (
              <AutoReportAndMoreWidget
                key={widget.widget_id}
                system={system}
                widget={widget}
                animals={animals}
                autoReports={autoReports}
                connect={{
                  connection: connect.connection,
                  domain: connect.domain
                }}
              />
            );
          case 3:
            return (
              <GraphWidget
                key={widget.widget_id}
                widget={widget}
                animals={animals}
              />
            );
          case 4: 
          return (
            <ProgressWidget
              key={widget.widget_id}
              widget={widget}
              animals={animals}
              connect={{
                connection: connect.connection,
                domain: connect.domain
              }}
            />
          );
          default:
            return null;
        }
      });
  }, [widgetsToShow, connect.connection, connect.domain, system, animals, autoReports]);
  
  // #endregion

  // Widget Content
  const content = useMemo(() => {
    if (renderedWidgets.length === 0) {
      return <p style={{...css.emptyPen, color: colors.greyLightTertiary, textAlign: "center"}}>{t("widgets.general.noWidgets", {defaultValue: "No widgets to show"})}</p>;
    } else if (overrides && overrides.columnCount !== undefined) {
      return <div style={{ columnCount: overrides && overrides.columnCount ? overrides.columnCount : undefined, columnGap: "20px" }}>
        {renderedWidgets.map((widget, index) => (
          <div key={index} style={{ breakInside: "avoid", marginBottom: "20px" }}>
            {widget}
          </div>
        ))}
      </div>;
    } else {
      return renderedWidgets;
    }
  }, [overrides, renderedWidgets])

  return content;
}